import { memo, useMemo } from "react";
import { SelectField } from "@edgetier/select";
import { IProps } from "./filtered-select-field.types";
import "./filtered-select-field.scss";

/**
 * A select field who's options can be filtered.
 * @param props.allOptions      All the options without filtering.
 * @param props.isLoading       Boolean indicating if the options are loading.
 * @param props.filteredOptions The filtered options.
 */
const FilteredSelectField = <Option extends {}, Value extends {} = string>({
    allOptions,
    isLoading,
    filteredOptions,
    ...selectProps
}: IProps<Option, Value>) => {
    const difference = (allOptions?.length ?? 0) - (filteredOptions?.length ?? 0);

    const selectMenuMessage = useMemo(() => {
        if (typeof filteredOptions === "undefined") {
            return undefined;
        }

        if (isLoading) {
            return "...loading";
        }

        return difference > 0
            ? `${difference} ${selectProps.description ?? "option"}s removed by other filters`
            : undefined;
    }, [difference, filteredOptions, isLoading, selectProps.description]);

    return (
        <SelectField<Option, Value>
            message={selectMenuMessage}
            isLoading={isLoading}
            items={filteredOptions ?? allOptions ?? []}
            useAllItemsSelectedPreviewMessage={difference === 0}
            {...selectProps}
        />
    );
};

export default memo(FilteredSelectField) as typeof FilteredSelectField;
